/**
 * Type-attribute validation shared between client and worker.
 *
 * The attribute-panel renders a form generated from `AttributeSchema[]`
 * (per element-type or per relation-type). Each schema entry has a
 * `type` like `"string"`, `"int?"`, `"enum"`, `"date?"`, etc. — we
 * validate user-supplied values against those types here so:
 *   - the client can show inline error markers + tooltip
 *   - the worker can reject invalid PATCH bodies with a 400 + Problem
 *     Detail referencing the bad field
 *
 * Convention: a trailing `?` makes the type *optional* (null / undefined
 * / empty-string accepted). Without `?` the value is required.
 */
import type { AttributeSchema } from "./registry";

export interface AttributeValidationError {
  /** Schema field name (matches the key under `concept.attributes`). */
  name: string;
  /** Stable machine-readable code — useful for i18n on the client. */
  code: "required" | "wrong_type" | "not_in_enum" | "bad_date" | "bad_int" | "empty_array_item";
  /** Short human message in NL — UI may translate via the code. */
  message: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Optional flags that change which checks `validateAttribute` enforces.
 * Default: full validation including required-presence-check.
 *
 * `skipRequired` — used by the worker's create-handlers (POST concept,
 * POST relation) so dragging a type op een diagram niet faalt op
 * required-but-empty attributes. De gebruiker vult ze in via het
 * attributenpaneel; de PATCH-flow valideert dan wel volledig.
 * Type-correctness wordt nog steeds gecheckt — alleen de "absent + niet
 * optional → required-error" stap wordt overgeslagen.
 */
export interface ValidateOptions {
  skipRequired?: boolean;
}

/**
 * **TBD/NTB-sentinel-waarden** (M5-§6) — modelleur kan een required
 * veld vullen met deze placeholder-tekst om aan te geven "weet ik
 * nog niet" zonder de validatie te breken. TBD = To Be Determined
 * (Engels), NTB = Nog Te Bepalen (Nederlands). Case-insensitive.
 *
 * Acceptatie per type:
 * - `string` / `string[]`: TBD/NTB als string-value passes
 * - `enum`: TBD/NTB passes ook als 't niet in `options` staat — alleen
 *   de exacte sentinel, geen willekeurige string. Zo kan een verplicht
 *   keuzeveld zichtbaar "nog in te vullen" blijven in plaats van stil
 *   de eerste optie te krijgen (zie `fillMissingRequiredAttributes`).
 *   Een pack-author mag "tbd"/"ntb" ook expliciet aan `options` toevoegen
 *   (bv. c4.ContainerInstance.environment); dan wint die optie.
 * - `bool` / `int` / `date`: GEEN TBD-acceptance — die types hebben
 *   geen valid sentinel-value. Modelleur moet een echte waarde
 *   kiezen, of pack-author maakt 't veld optional (`bool?`)
 *
 * Een TBD-overzicht-rapport per workspace (M5-§6 vervolg) zal alle
 * concepten/relaties met TBD-velden lijsten zodat ze later
 * complete kunnen worden gemaakt.
 */
export const TBD_SENTINELS = ["tbd", "ntb"] as const;

/** Case-insensitive check of een waarde een TBD/NTB-sentinel is. */
export function isTbdSentinel(value: unknown): boolean {
  return typeof value === "string" && TBD_SENTINELS.includes(value.trim().toLowerCase() as never);
}

/**
 * Validate a single attribute value against its schema. Returns null on
 * pass, an error object on fail. Pull this out so both bulk-validation
 * (`validateAttributes`) and inline field-level checks share the same
 * rules.
 */
export function validateAttribute(
  schema: AttributeSchema,
  value: unknown,
  options: ValidateOptions = {},
): AttributeValidationError | null {
  const optional = schema.type.endsWith("?");
  const baseType = optional ? schema.type.slice(0, -1) : schema.type;

  // Treat null / undefined / "" as "absent". For required types this is
  // an error; for optional types it's a pass. Voor bilingual_string
  // geldt absent als beide nl + en leeg zijn.
  const absent =
    value === null ||
    value === undefined ||
    value === "" ||
    (baseType === "bilingual_string" &&
      typeof value === "object" &&
      value !== null &&
      ((value as Record<string, unknown>).nl === undefined ||
        (value as Record<string, unknown>).nl === "") &&
      ((value as Record<string, unknown>).en === undefined ||
        (value as Record<string, unknown>).en === ""));
  if (absent) {
    if (optional) return null;
    if (options.skipRequired) return null;
    return {
      name: schema.name,
      code: "required",
      message: `Verplicht veld "${schema.name}" is leeg.`,
    };
  }

  switch (baseType) {
    case "string":
      if (typeof value !== "string") {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet tekst zijn.`,
        };
      }
      // TBD/NTB-sentinel passes voor required string-velden. Geen
      // aparte error-code want 't is een geldige waarde, geen fout.
      // De UI kan via isTbdSentinel(value) detecteren dat 't een
      // placeholder is en bv. een gele highlight tonen.
      return null;

    case "bilingual_string": {
      // Object-vorm met optional nl + en. Tenminste één van beide moet
      // niet-leeg zijn (de absent-check hierboven dekt dat al voor
      // required); bij valide-shape-zonder-content kan de andere taal
      // automatisch via translate worden aangevuld in de UI.
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een tweetalig {nl, en}-object zijn.`,
        };
      }
      const obj = value as Record<string, unknown>;
      if (obj.nl !== undefined && typeof obj.nl !== "string") {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}.nl" moet tekst zijn.`,
        };
      }
      if (obj.en !== undefined && typeof obj.en !== "string") {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}.en" moet tekst zijn.`,
        };
      }
      return null;
    }

    case "bool":
      if (typeof value !== "boolean") {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet aan/uit zijn.`,
        };
      }
      return null;

    case "int":
      if (typeof value !== "number" || !Number.isInteger(value)) {
        return {
          name: schema.name,
          code: "bad_int",
          message: `"${schema.name}" moet een geheel getal zijn.`,
        };
      }
      return null;

    case "color":
      if (typeof value !== "string" || !/^#[0-9A-Fa-f]{6}$/.test(value)) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een hex-kleur zijn in #RRGGBB-formaat.`,
        };
      }
      return null;

    case "date":
      if (typeof value !== "string" || !DATE_RE.test(value)) {
        return {
          name: schema.name,
          code: "bad_date",
          message: `"${schema.name}" moet een datum zijn in YYYY-MM-DD-formaat.`,
        };
      }
      // Reject impossible dates that pass the regex (e.g. 2025-02-30).
      // `Date.parse` is lenient, so cross-check the parsed pieces match.
      {
        const [y, m, d] = value.split("-").map((s) => Number.parseInt(s, 10));
        const probe = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
        if (
          probe.getUTCFullYear() !== y ||
          probe.getUTCMonth() + 1 !== m ||
          probe.getUTCDate() !== d
        ) {
          return {
            name: schema.name,
            code: "bad_date",
            message: `"${schema.name}" is geen bestaande datum.`,
          };
        }
      }
      return null;

    case "enum":
      if (typeof value !== "string") {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een tekst-keuze zijn.`,
        };
      }
      // Open-enum: `options` dient als suggestie, niet als sluitend
      // domein. Elke non-empty string is geldig zodra `freeform: true`.
      if (schema.freeform) return null;
      // TBD/NTB-placeholder: verplicht keuzeveld dat nog ingevuld moet
      // worden. Geldig, net als bij tekstvelden; de UI en het
      // Validatie-rapport tonen 'm als "nog te bepalen".
      if (isTbdSentinel(value)) return null;
      if (!schema.options?.includes(value)) {
        return {
          name: schema.name,
          code: "not_in_enum",
          message: `"${schema.name}" = "${value}" zit niet in de toegestane lijst (${schema.options?.join(", ") ?? "geen opties gedefinieerd"}).`,
        };
      }
      return null;

    case "string[]":
      if (!Array.isArray(value)) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een lijst zijn.`,
        };
      }
      for (const item of value) {
        if (typeof item !== "string" || item.length === 0) {
          return {
            name: schema.name,
            code: "empty_array_item",
            message: `"${schema.name}" bevat een lege of niet-tekstuele waarde.`,
          };
        }
      }
      return null;

    case "bilingual_string[]": {
      // Shape: { nl: string[], en: string[] } — twee onafhankelijke
      // lijsten per taal. Synoniemen-use-case: een NL-term kan
      // andere/meer/minder synoniemen hebben dan z'n EN-tegenhanger.
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een tweetalig {nl: [...], en: [...]}-object zijn.`,
        };
      }
      const obj = value as Record<string, unknown>;
      for (const key of ["nl", "en"]) {
        const arr = obj[key];
        if (arr === undefined) continue;
        if (!Array.isArray(arr)) {
          return {
            name: schema.name,
            code: "wrong_type",
            message: `"${schema.name}.${key}" moet een lijst van strings zijn.`,
          };
        }
        for (const item of arr) {
          if (typeof item !== "string" || item.length === 0) {
            return {
              name: schema.name,
              code: "empty_array_item",
              message: `"${schema.name}.${key}" bevat een lege of niet-tekstuele waarde.`,
            };
          }
        }
      }
      return null;
    }

    case "reference":
      // Single-reference naar een ander concept. Waarde = concept-id-string.
      // We valideren alleen de shape (non-empty string); referential-
      // integrity-check (target-concept bestaat nog) gebeurt soft op
      // render-tijd en is geen save-blocker. Een verwijderd target
      // moet bewerkbaar blijven zodat de modelleur de stale ref kan
      // opruimen.
      if (typeof value !== "string" || value.length === 0) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een element-id zijn.`,
        };
      }
      return null;

    case "reference[]":
      // Multi-reference: lijst van concept-ids. Lege array OK voor
      // optionele velden (al gedekt door absent-check); voor required
      // wordt een lege array hier door de absent-check niet als absent
      // beschouwd dus we accepteren 'm. Dieper duplicate-check overslaan.
      if (!Array.isArray(value)) {
        return {
          name: schema.name,
          code: "wrong_type",
          message: `"${schema.name}" moet een lijst van element-ids zijn.`,
        };
      }
      for (const item of value) {
        if (typeof item !== "string" || item.length === 0) {
          return {
            name: schema.name,
            code: "empty_array_item",
            message: `"${schema.name}" bevat een lege of niet-tekstuele waarde.`,
          };
        }
      }
      return null;

    default:
      // Unknown schema type — skip silently. The field-renderer already
      // shows an "unsupported type" hint to the user, and we don't want
      // to block PATCHes on schemas we don't understand yet.
      return null;
  }
}

/**
 * Run `validateAttribute` over every schema entry and return all errors.
 * Empty array → values are valid. Order-stable: errors come back in the
 * same order as `schemas`.
 */
export function validateAttributes(
  schemas: readonly AttributeSchema[],
  values: Record<string, unknown>,
  options: ValidateOptions = {},
  /**
   * Optional — top-level concept-fields die door virtuele
   * `storage_mode`-attributen (concept_name / concept_description)
   * worden gelezen. Wanneer afwezig: virtuele velden worden tegen
   * `values[name]` gevalideerd (legacy-pad voor pre-migratie data,
   * zonder errors).
   */
  conceptFields?: { description?: unknown; name?: unknown },
): AttributeValidationError[] {
  const errors: AttributeValidationError[] = [];
  for (const schema of schemas) {
    let value: unknown;
    if (schema.storage_mode === "concept_description" || schema.storage_mode === "concept_name") {
      // Virtuele opslag — lees uit concept.description of concept.name.
      // Als die kernel-slot leeg is en values[name] wel een waarde heeft
      // (legacy-data van vóór migration), val daar nog op terug zodat
      // oude rijen niet plotseling required-violations geven.
      const fromConcept =
        schema.storage_mode === "concept_description"
          ? conceptFields?.description
          : conceptFields?.name;
      const isEmpty =
        fromConcept === null ||
        fromConcept === undefined ||
        (typeof fromConcept === "object" &&
          fromConcept !== null &&
          ((fromConcept as { nl?: string; en?: string }).nl ?? "") === "" &&
          ((fromConcept as { nl?: string; en?: string }).en ?? "") === "");
      value = isEmpty ? values[schema.name] : fromConcept;
    } else {
      value = values[schema.name];
    }
    const err = validateAttribute(schema, value, options);
    if (err) errors.push(err);
  }
  return errors;
}

/**
 * Vul ontbrekende **required** attributen aan met pack-defaults zodat
 * `validateAttributes` slaagt zonder dat de gebruiker een
 * "1 field(s) invalid"-foutmelding krijgt op velden die hij nooit
 * heeft aangeraakt.
 *
 * Wanneer dit nodig is:
 *   - **Drag-from-palette / paste-from-text**: nieuw concept of nieuwe
 *     relatie krijgt automatisch z'n type-defaults (zie `concepts.ts`
 *     en `relations.ts` create-handlers — dat pad gebruikt nu nog
 *     `defaultAttributesForType` in `apps/web/src/state/workspace.ts`)
 *   - **Type-wisseling van een bestaand object**: oude attributes
 *     uit het vorige type-schema overlappen mogelijk niet met het
 *     nieuwe — alle ontbrekende required velden krijgen hun default
 *   - **Hydrate van bestaande data na een schema-uitbreiding**: een
 *     concept/relatie die vóór een pack-update gemaakt is, mist de
 *     nieuwe required velden. Backfill via deze helper voorkomt dat
 *     elke patch-flow daarna faalt op validatie (bug F in
 *     `M4-acceptance.md`)
 *
 * Defaults per type:
 *   - `enum`: de TBD/NTB-placeholder — de "tbd"/"ntb"-optie als het pack
 *     die declareert, anders `"TBD"`. Nooit de eerste optie: die zou eruit
 *     zien als een echte keuze van de modelleur. Zonder options (dynamische
 *     keuzelijst) blijft het veld leeg.
 *   - `bool`: `false`
 *   - `int`: `0`
 *   - `string`: `""`
 *   - `string[]`: `[]` (passeert de absent-check zonder items-lus)
 *   - `date`: geen blanket default — de caller / UI moet de gebruiker
 *     prompten
 *
 * Optionele velden (type eindigt op `?`) worden NOOIT gevuld — die
 * mogen leeg blijven. Bestaande waarden worden NOOIT overschreven —
 * alleen aanwezige `null` / `undefined` / `""` (de "absent"-keys
 * volgens `validateAttribute`) krijgen een default. Dat houdt het pad
 * idempotent: meerdere keren aanroepen verandert niets.
 */
export function fillMissingRequiredAttributes(
  schemas: readonly AttributeSchema[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...values };
  for (const schema of schemas) {
    if (schema.type.endsWith("?")) continue; // optional — laat leeg
    // Een veld met `storage_mode` leeft in de naam of beschrijving van het
    // concept, en de validator leest 'm daar ook. Een sentinel hier zou een
    // tweede, verouderde kopie in `attributes` zetten die het TBD-rapport
    // blijft melden terwijl de beschrijving allang is ingevuld.
    if (schema.storage_mode) continue;
    const current = out[schema.name];
    const absent = current === null || current === undefined || current === "";
    if (!absent) continue; // bestaande waarde respecteren
    const baseType = schema.type;
    switch (baseType) {
      case "enum": {
        // Zelfde placeholder-conventie als string: zichtbaar "nog te
        // bepalen" i.p.v. stil de eerste optie (die las als een echte
        // keuze — bv. een Container Instance die zomaar `dev` werd).
        const opts = schema.options ?? [];
        if (opts.length === 0 && !schema.freeform) break; // dynamische lijst
        out[schema.name] = opts.find((o) => isTbdSentinel(o)) ?? "TBD";
        break;
      }
      case "bool":
        out[schema.name] = false;
        break;
      case "int":
        out[schema.name] = 0;
        break;
      case "string":
        // TBD-sentinel ipv "" — de validator behandelt "" als absent
        // ("required-violation"), dus filling met lege string zou een
        // server-side reject geven bij required-string-velden zoals
        // sharp.Concept.definition_text. TBD passeert wel én geeft de
        // gebruiker een duidelijke placeholder om in te vullen later
        // (zie isTbdSentinel-helper voor UI-highlighting).
        out[schema.name] = "TBD";
        break;
      case "bilingual_string":
        // Sentinel-conventie: NTB (Nog Te Bepalen) in NL + TBD (To Be
        // Determined) in EN. Beide passeren de validator; gebruiker
        // ziet meteen taal-juiste placeholders in plaats van een gat.
        out[schema.name] = { nl: "NTB", en: "TBD" };
        break;
      case "string[]":
        out[schema.name] = [];
        break;
      case "bilingual_string[]":
        // Twee lege lijsten — de gebruiker vult zelf in. Validator
        // accepteert lege arrays per taal.
        out[schema.name] = { nl: [], en: [] };
        break;
      case "reference[]":
        // Lege referentie-lijst passes de validator (zie validateAttribute
        // reference[]). Zonder deze fill blijft een required-reference[]-veld
        // absent en faalt élke PATCH op required-violation — bv. bij
        // stereotype-switch in de AMEFF-review-modal naar risk.Vulnerability
        // dat compensating_controls: reference[] required heeft.
        out[schema.name] = [];
        break;
      // date / reference: geen blanket default — laat de caller/UI prompten
    }
  }
  return out;
}

/**
 * Upgrade legacy plain-string waarden naar `{nl, en}`-objects voor
 * schema-velden die nu `bilingual_string` / `bilingual_string?` zijn.
 *
 * **Waarom**: in [379e0ac] zijn 27 stereotype-attrs van `string` naar
 * `bilingual_string` geconverteerd (definition_text, description_text,
 * tech_stack_description, etc.). Bestaande concepten/relaties in de DB
 * hebben die velden nog als plain-string opgeslagen — bv. `"TBD"` uit
 * de oude fillMissingRequiredAttributes (vóór 2a26922). Server-side
 * `validateAttribute` voor bilingual_string verwacht een object en
 * geeft `wrong_type` terug op een plain-string → 400 op élke PATCH,
 * óók eentje die alleen een ander veld (bv. een datum-pick) aanraakt.
 *
 * Symmetrisch met de front-end auto-spread in `fields.tsx`:
 * - TBD/NTB-sentinel → `{nl: "NTB", en: "TBD"}` (taal-juiste placeholders)
 * - Andere plain-string → `{nl: <oud>, en: ""}` (laat gebruiker zelf
 *   vertalen, of triggert auto-translate bij blur)
 *
 * Idempotent: aanwezige `{nl, en}`-objects blijven onaangeroerd. Niet-
 * bilingual schema-velden idem.
 *
 * Aan te roepen vóór `validateAttributes` in PATCH-handlers zodat
 * legacy-data niet de hele edit-flow blokkeert.
 */
export function upgradeLegacyBilingualAttributes(
  schemas: readonly AttributeSchema[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  let mutated = false;
  const out: Record<string, unknown> = { ...values };
  for (const schema of schemas) {
    const baseType = schema.type.endsWith("?") ? schema.type.slice(0, -1) : schema.type;
    if (baseType === "bilingual_string") {
      const current = out[schema.name];
      if (typeof current !== "string") continue; // al object of absent — laat staan
      if (isTbdSentinel(current)) {
        out[schema.name] = { nl: "NTB", en: "TBD" };
      } else {
        out[schema.name] = { nl: current, en: "" };
      }
      mutated = true;
    } else if (baseType === "bilingual_string[]") {
      // Legacy `string[]` (bv. oude definition_synonyms-data) → mappen
      // naar de NL-lijst, EN leeg laten zodat de gebruiker zelf kan
      // vertalen of aanvullen.
      const current = out[schema.name];
      if (!Array.isArray(current)) continue;
      out[schema.name] = { nl: current as string[], en: [] };
      mutated = true;
    }
  }
  return mutated ? out : values;
}

/**
 * Migreer legacy `{source,target}_cardinality_{min,max}`-int-quad naar
 * de nieuwe `{source,target}_cardinality`-enum (Sharp ConceptAssociation
 * spec-update 2026-05-09 — crow's-foot-notatie kent vier waarden).
 *
 * Mapping (`-1` = onbeperkt-marker uit oude opslag):
 * - `(0,  1)` → `zero_or_one`
 * - `(1,  1)` → `one`
 * - `(0, -1)` → `zero_or_many`
 * - `(1, -1)` → `one_or_many`
 * - andere combinaties (bv. `(2, 5)`) → `zero_or_many` (maximaal
 *   permissief; bounded ranges horen sowieso in een
 *   `«Concept Constraint»` per spec, niet op de Association)
 *
 * Idempotent: nieuwe enum-velden blijven onaangeroerd; oude int-velden
 * worden gedropt na conversie zodat ze niet meereizen door de DB als
 * dead data.
 *
 * Aan te roepen vóór `validateStereotypeAttributes` op
 * `sharp.ConceptAssociation` / `sharp.IdentifyingAssociation`-relaties
 * zodat legacy-data niet de hele edit-flow blokkeert.
 */
export function migrateLegacySharpCardinality(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const sides: Array<"source" | "target"> = ["source", "target"];
  let mutated = false;
  const out: Record<string, unknown> = { ...values };
  for (const side of sides) {
    const newKey = `${side}_cardinality`;
    const minKey = `${side}_cardinality_min`;
    const maxKey = `${side}_cardinality_max`;
    const hasOldKeys = minKey in out || maxKey in out;
    if (!hasOldKeys) continue;
    // Drop de oude keys altijd zodat ze niet als dead data
    // meereizen (validateAttributes negeert ze toch — schema kent ze
    // niet meer — maar opruimen is netter).
    delete out[minKey];
    delete out[maxKey];
    mutated = true;
    // Alleen new-key invullen wanneer er nog geen waarde staat.
    // Anders heeft de gebruiker (of een eerdere migratie-pass) hem al
    // gezet en respecteren we dat.
    if (typeof out[newKey] === "string") continue;
    const min = values[minKey];
    const max = values[maxKey];
    out[newKey] = mapCardinalityIntPairToEnum(
      typeof min === "number" ? min : null,
      typeof max === "number" ? max : null,
    );
  }
  return mutated ? out : values;
}

function mapCardinalityIntPairToEnum(min: number | null, max: number | null): string {
  // -1 in de oude opslag betekende 'onbeperkt' (Sharp '*' / 'N').
  if (min === 0 && max === 1) return "zero_or_one";
  if (min === 1 && max === 1) return "one";
  if (min === 0 && (max === -1 || max === null)) return "zero_or_many";
  if (min === 1 && (max === -1 || max === null)) return "one_or_many";
  // Alles wat niet schoon mapt — incl. bounded ranges zoals (2,5) —
  // valt naar de meest permissieve waarde. Bounded ranges horen
  // volgens spec in een «Concept Constraint» en niet op de relatie.
  return "zero_or_many";
}
